/**
 * Live cross-tab / cross-app session resync for the standalone Sandbox.
 *
 * A cookie flag alone can't update a tab that's already running. This listener
 * re-reads the shared cy-sso cookies whenever the tab regains focus / becomes
 * visible, and adopts them (via saveSession) when they carry a different token
 * than the one currently in `localStorage.sandbox_user`.
 *
 * Concretely this is the "user switches profile in StudentPortal, then returns
 * to an already-open Sandbox tab" fix. The shared cookies are the source of
 * truth (the portal re-writes them on every login/switch), so the open Sandbox
 * tab simply trusts whatever the shared cookie now holds.
 *
 * Standalone only — in embedded (MFE) mode the portal drives token updates via
 * the SandboxRenderer + mfeRefreshCoordinator.
 */

import jwtDecode from "jwt-decode";
import { getCookie, isLoggedOut } from "./cookieUtils";
import { decrypt } from "./others";
import { saveSession } from "./auth";
import { isNewerToken, tokenRole } from "../../Services/tokenManager";
import sessionExpired from "../sessionExpired";
import { LOGOUT_REASONS } from "../../utils/authEventLog";
import { logSessionRecovered } from "../../Services/recoveryDiagnostics";

const tokenUserId = (token) => {
  try {
    const d = jwtDecode(token) || {};
    return d.userId || d.studentId || d.sub || null;
  } catch {
    return null;
  }
};

const resyncFromCookies = () => {
  try {
    // Only a STUDENT session participates in cross-app auto-login/auto-logout
    // (symmetric with the student-only cookie WRITE in saveSession and the
    // student-only setLoggedOutFlag). A mentor/admin session is app-local:
    // since its refreshes never re-write the shared cookies, any cookie seen
    // here is by definition stale for it — adopting one would replace live
    // tokens with rotated-away ones and kill the session on the next request
    // (the post-handoff mentor logout bug). It must also not be torn down by a
    // student's cy_logged_out signal on a shared browser.
    let current = {};
    try {
      current = decrypt(localStorage.getItem("sandbox_user")) || {};
    } catch {
      current = {};
    }
    if (current.role && current.role !== "student") return;

    // Cross-app logout: the other app raised `cy_logged_out`. If we still hold a
    // session here, end it now that the tab is back in focus.
    if (isLoggedOut()) {
      if (localStorage.getItem("sandbox_user")) {
        sessionExpired(LOGOUT_REASONS.LOGGED_OUT_IN_OTHER_TAB, { trigger: "focus" });
      }
      return;
    }

    const accessToken = getCookie("Authorization");
    const refreshToken = getCookie("refresh-token");
    if (!accessToken || !refreshToken) return;

    // Only act when already logged in here — boot bootstrap owns first-paint
    // login on a fresh tab.
    if (!localStorage.getItem("sandbox_user")) return;

    const localToken = current.ACCESS_TOKEN || current.Authorization;

    // Token unchanged → nothing to do (guards against loops).
    if (localToken === accessToken) return;

    // Non-student cookies are handoff transport (Epicenter → SSOLanding), not a
    // sync source — never adopt them into a running session.
    if (tokenRole(accessToken) !== "student") return;

    // Freshness guard: adopt only a token issued STRICTLY LATER than ours.
    // Every legitimate sync source (login, profile switch, refresh) mints a
    // brand-new token, so a not-newer cookie is stale by definition — adopting
    // it would downgrade a live session to rotated-away tokens.
    if (!isNewerToken(accessToken, localToken)) return;

    // Did the underlying user/student change (a profile switch in the portal),
    // or is this just a same-user token refresh?
    const differentUser = tokenUserId(localToken) !== tokenUserId(accessToken);

    // For a different user, rebuild from the new token's JWT (don't carry over
    // the previous profile's fields). For a same-user refresh, preserve them.
    saveSession({
      accessToken,
      refreshToken,
      userData: differentUser ? {} : current,
    });
    // Same student = the portal refreshed while this tab was away (a
    // recovery); another student = a profile switch, not counted.
    if (!differentUser) {
      logSessionRecovered({ stage: "focus-resync", source: "cookie", staleToken: localToken });
    }

    // A same-user token refresh updates in-memory tokens and is picked up by the
    // next request — no reload needed. But an actual account/profile switch must
    // re-render the whole app with the new student's data, so reload.
    if (differentUser) {
      window.location.reload();
    }
  } catch (_) {
    /* never throw from a focus handler */
  }
};

let registered = false;

export const registerAuthSyncListener = () => {
  if (registered || typeof window === "undefined") return;
  registered = true;

  window.addEventListener("focus", resyncFromCookies);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") resyncFromCookies();
  });
};

export default registerAuthSyncListener;
